// src/components/Admin/RadioStationManagementSection.js

import React, { useState } from 'react';
import { useAppContext } from '../../App';
import { api, BASE_URL } from '../../apiService';
import { toast } from 'react-toastify';
import ConfirmationModal from '../Common/ConfirmationModal';
import * as Sentry from "@sentry/react";

function RadioStationManagementSection() {
    const { allStations, setAllStations } = useAppContext();
    
    const radioStations = (allStations || []).filter(s => {
        const frequency = (s.frequency || '').trim().toUpperCase();
        const city = (s.city || '').trim().toUpperCase();
        return frequency && frequency !== 'N/A' && city !== 'N/A' && !(s.name || '').toLowerCase().includes('flare');
    });
    const flareBusinesses = (allStations || []).filter(s => !radioStations.some(r => r.id === s.id));

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [deletingStation, setDeletingStation] = useState(null);

    const [showForm, setShowForm] = useState(false);
    const [editingStation, setEditingStation] = useState(null);
    const [isFlareBusiness, setIsFlareBusiness] = useState(false);
    const [stationName, setStationName] = useState('');
    const [frequency, setFrequency] = useState('');
    const [city, setCity] = useState('');
    const [logoFile, setLogoFile] = useState(null);
    const [logoPreview, setLogoPreview] = useState('');

    const handleEditClick = (station) => {
        const stationFrequency = (station.frequency || '').trim().toUpperCase();
        const flareMode = stationFrequency === 'N/A';
        setIsFlareBusiness(flareMode);
        setEditingStation(station);
        setStationName(station.name);
        setFrequency(flareMode ? 'N/A' : station.frequency);
        setCity(station.city);
        setLogoPreview(station.logoUrl);
        setLogoFile(null);
        setShowForm(true);
    };
    
    const handleAddNewClick = () => {
        setEditingStation(null);
        setIsFlareBusiness(false);
        setStationName(''); setFrequency(''); setCity('');
        setLogoFile(null); setLogoPreview('');
        setShowForm(true);
    };

    const handleAddFlareBusinessClick = () => {
        const existing = allStations.find(s => (s.name || '').toLowerCase().includes('flare'));
        if (existing) {
            handleEditClick(existing);
            toast.info("Flare business already exists. Editing it now.");
            return;
        }
        setEditingStation(null);
        setIsFlareBusiness(true);
        setStationName('Flare Magazine');
        setFrequency('N/A');
        setCity('Karachi');
        setLogoFile(null);
        setLogoPreview('');
        setShowForm(true);
    };
    
    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setLogoFile(file);
            setLogoPreview(URL.createObjectURL(file));
        }
    };
    
    const handleFormSubmit = async (e) => {
        e.preventDefault();
        if (!stationName || !frequency || !city) {
            toast.error("All fields are required."); return;
        }
        setIsSubmitting(true);

        try {
            let logoUrl = editingStation?.logoUrl || '';
            if (logoFile) {
                const formData = new FormData();
                formData.append('photo', logoFile);
                const response = await api.post('upload', formData, true);
                logoUrl = response.filePath;
            }

            const generateId = () => Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
            const stationData = { 
                name: stationName, 
                frequency: isFlareBusiness ? 'N/A' : frequency, 
                city, 
                logoUrl 
            };
            
            if (editingStation) {
                await api.put('radio_stations', editingStation.id, stationData);
                toast.success("Station updated successfully!");
            } else {
                if(!logoUrl) {
                    toast.error("Logo is required for a new station.");
                    setIsSubmitting(false); return;
                }
                stationData.id = generateId();
                await api.post('radio_stations', stationData);
                toast.success("Station added successfully!");
            }
            setShowForm(false);
            const updatedStations = await api.get('radio_stations');
            setAllStations(updatedStations);
        } catch (err) {
            toast.error(`Operation failed: ${err.message}`);
            if(window.Sentry) Sentry.captureException(err);
        } finally {
            setIsSubmitting(false);
        }
    };
    
    const handleDeleteStation = async () => {
        if (!deletingStation) return;
        try {
            await api.delete('radio_stations', deletingStation.id);
            setAllStations(prev => prev.filter(s => s.id !== deletingStation.id));
            toast.success("Station deleted successfully.");
        } catch (err) {
            toast.error(`Failed to delete station: ${err.message}`);
            if(window.Sentry) Sentry.captureException(err);
        } finally {
            setDeletingStation(null);
        }
    };

    return (
        <div className="container mx-auto">
            <ConfirmationModal isOpen={!!deletingStation} onClose={() => setDeletingStation(null)} onConfirm={handleDeleteStation} title="Delete Radio Station">
                Are you sure you want to delete <strong>{deletingStation?.name}</strong>? This action cannot be undone.
            </ConfirmationModal>

            <div className="mb-8 flex justify-between items-center">
                <div>
                    <h1 className="text-4xl font-bold text-gray-800">Manage Radio Stations</h1>
                    <p className="text-gray-500 mt-1">Add, edit, or remove stations from your network.</p>
                </div>
                 {!showForm && (
                    <div className="flex gap-3">
                        <button onClick={handleAddNewClick} className="bg-rose-600 text-white font-bold py-3 px-6 rounded-lg shadow-lg hover:bg-rose-700 transition-all">+ Add New Station</button>
                        <button onClick={handleAddFlareBusinessClick} className="bg-slate-700 text-white font-bold py-3 px-6 rounded-lg shadow-lg hover:bg-slate-600 transition-all">+ Add Flare Business</button>
                    </div>
                 )}
            </div>

            {showForm && (
                <div className="bg-white p-8 rounded-2xl shadow-lg mb-8">
                    <h2 className="text-2xl font-bold text-gray-800 mb-6">{editingStation ? 'Edit Station' : 'Add New Station'}</h2>
                    <form onSubmit={handleFormSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                           <label className="block text-sm font-medium text-gray-700">Station Name</label>
                           <input type="text" value={stationName} onChange={e => setStationName(e.target.value)} className="mt-1 w-full p-3 bg-gray-50 rounded-md" required />
                        </div>
                        <div>
                           <label className="block text-sm font-medium text-gray-700">Frequency</label>
                           <input type="text" value={isFlareBusiness ? 'N/A' : frequency} onChange={e => setFrequency(e.target.value)} placeholder="e.g., 99.0 FM" className="mt-1 w-full p-3 bg-gray-50 rounded-md" required disabled={isFlareBusiness}/>
                        </div>
                        <div>
                           <label className="block text-sm font-medium text-gray-700">City</label>
                           <input type="text" value={city} onChange={e => setCity(e.target.value)} placeholder="e.g., Lahore" className="mt-1 w-full p-3 bg-gray-50 rounded-md" required />
                        </div>
                         <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Station Logo</label>
                            {logoPreview && <img src={logoPreview.startsWith('blob:') || logoPreview.startsWith('http') ? logoPreview : `${BASE_URL}${logoPreview}`} alt="Logo preview" className="h-20 w-auto mb-2 border p-1 rounded-md"/>}
                            <input type="file" onChange={handleFileChange} accept="image/*" className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-rose-50 file:text-rose-700 hover:file:bg-rose-100"/>
                         </div>
                         <div className="md:col-span-2 flex justify-end gap-4 pt-6 border-t">
                            <button type="button" onClick={() => setShowForm(false)} className="bg-gray-300 text-gray-800 font-bold py-3 px-8 rounded-lg">Cancel</button>
                            <button type="submit" disabled={isSubmitting} className="bg-rose-600 text-white font-bold py-3 px-8 rounded-lg shadow-lg hover:bg-rose-700 transition-all disabled:bg-gray-400">
                                {isSubmitting ? "Saving..." : "Save Station"}
                            </button>
                         </div>
                    </form>
                </div>
            )}

            <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Station</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Frequency</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden md:table-cell">City</th>
                                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {radioStations.map(station => (
                                <tr key={station.id}>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center">
                                            <div className="flex-shrink-0 h-10 w-10">
                                                <img className="h-10 w-10 rounded-md object-contain" src={station.logoUrl?.startsWith('http') ? station.logoUrl : `${BASE_URL}${station.logoUrl}`} alt={station.name} onError={(e) => { e.target.src="https://placehold.co/40x40/CCCCCC/666666?text=Logo"; }}/>
                                            </div>
                                            <div className="ml-4">
                                                <div className="text-sm font-medium text-gray-900">{station.name}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{station.frequency}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 hidden md:table-cell">{station.city}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium space-x-4">
                                        <button onClick={() => handleEditClick(station)} className="text-blue-600 hover:text-blue-800 font-semibold no-underline">Edit</button>
                                        <button onClick={() => setDeletingStation(station)} className="text-red-600 hover:text-red-800 font-semibold no-underline">Delete</button>
                                    </td>
                                </tr>
                            ))}
                            {flareBusinesses.length > 0 && (
                                <tr>
                                    <td className="px-6 py-3 text-xs font-bold text-slate-600 bg-slate-50" colSpan="4">Flare Business</td>
                                </tr>
                            )}
                            {flareBusinesses.map(station => (
                                <tr key={station.id} className="bg-slate-50/40">
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center">
                                            <div className="flex-shrink-0 h-10 w-10">
                                                <img className="h-10 w-10 rounded-md object-contain" src={station.logoUrl?.startsWith('http') ? station.logoUrl : `${BASE_URL}${station.logoUrl}`} alt={station.name} onError={(e) => { e.target.src="https://placehold.co/40x40/CCCCCC/666666?text=Logo"; }}/>
                                            </div>
                                            <div className="ml-4">
                                                <div className="text-sm font-medium text-gray-900">{station.name || 'Flare Magazine'}</div>
                                                <div className="text-xs text-slate-500">Business</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{station.frequency || 'N/A'}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 hidden md:table-cell">{station.city || 'N/A'}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium space-x-4">
                                        <button onClick={() => handleEditClick(station)} className="text-blue-600 hover:text-blue-800 font-semibold no-underline">Edit</button>
                                        <button onClick={() => setDeletingStation(station)} className="text-red-600 hover:text-red-800 font-semibold no-underline">Delete</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

export default RadioStationManagementSection;
